import { useState } from 'react';
import { Card, Button, Select, LoadingSkeleton } from '@nexora/design-system';
import { useServiceOrders } from './useServiceOrders';

interface ServiceOrderPickerProps {
  /** Callback com o id da OS escolhida. */
  onSelect: (id: string) => void;
  /** Titulo/contexto exibido acima do seletor (ex: nome da tela). */
  label: string;
}

/**
 * Seletor de Ordem de Servico usado pelas paginas de detalhe/apontamento/
 * materiais/historico quando nenhum `orderId` e injetado por prop ou pela URL.
 *
 * Permite escolher uma OS existente (primeira pagina da lista) para operar —
 * util enquanto o roteamento da aplicacao nao injeta o id via rota.
 */
export function ServiceOrderPicker({ onSelect, label }: ServiceOrderPickerProps): JSX.Element {
  const [selected, setSelected] = useState('');
  const { data, isLoading, error } = useServiceOrders({ limit: 100 });

  const orders = data?.data ?? [];

  return (
    <Card padding="1.5rem">
      <h3 className="font-headline-sm text-headline-sm text-on-surface m-0 mb-xs">{label}</h3>
      <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs mb-md">
        Selecione uma ordem de serviço para continuar.
      </p>

      {isLoading && <LoadingSkeleton count={3} height="40px" />}

      {error && !isLoading && (
        <div className="state-error" role="alert">
          <p>Erro ao carregar ordens de serviço</p>
          <p className="state-detail">{(error as Error).message}</p>
        </div>
      )}

      {!isLoading && !error && orders.length === 0 && (
        <div className="state-empty">
          <p>Nenhuma ordem de serviço encontrada.</p>
        </div>
      )}

      {!isLoading && !error && orders.length > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-sm">
          <div style={{ flex: 1, minWidth: 200 }}>
            <Select
              label="Ordem de Serviço"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Selecione...</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.number} — {o.title}
                </option>
              ))}
            </Select>
          </div>
          <Button disabled={!selected} onClick={() => onSelect(selected)}>
            Abrir
          </Button>
        </div>
      )}

      <style>{`
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1rem; color: #74777f; }
      `}</style>
    </Card>
  );
}